export type Story = {
  quote: string
  name: string
  context: string
  category: 'workshop' | 'maekerscafe' | 'general' | 'koffiebar'
  slug?: string
  fullStory?: string
}

export const stories: Story[] = [
  // General / Homepage
  {
    quote:
      'Ik kwam voor een workshop en vond een heel netwerk van vrouwen die dezelfde passie delen. De Maekerij voelt als thuiskomen.',
    name: 'Sarah',
    context: 'Vaste bezoeker sinds 2024',
    category: 'general',
    slug: 'sarah-thuiskomen',
    fullStory: `Twee jaar geleden zag ik op sociale media een workshop keramiek voorbijkomen bij De Maekerij. Ik had altijd al iets met klei willen doen, maar durfde nooit de stap te zetten. "Ik ben toch niet creatief," dacht ik.

Maar die avond veranderde alles. De docente was zo geduldig, de andere deelneemsters zo hartelijk — het voelde meteen vertrouwd. Ik ging naar huis met een scheef maar zelfgemaakt kommetje en een gevoel dat ik al lang niet meer had gehad: trots.

De week erna ging ik naar het Maekerscafé Draadgeluk. Gewoon om te kijken. Ik had mijn breiwerk meegenomen en voor ik het wist zat ik in een gesprek over patronen, over het leven, over alles en niets. Die vrouwen kenden me niet, maar ze maakten plaats alsof ik er altijd al bij had gehoord.

Nu, twee jaar later, kom ik elke week. Soms voor een workshop, soms gewoon voor een koffie en een praatje. Ik heb hier vriendinnen gemaakt die ik anders nooit had ontmoet. De Maekerij is voor mij geen gebouw — het is een gemeenschap. Het voelt als thuiskomen.`,
  },
  {
    quote:
      'Wat begon als een avondje uit, is uitgegroeid tot mijn favoriete plek in de stad. Elke week kijk ik uit naar het maekerscafé.',
    name: 'Annelies',
    context: 'Maekerscafé Draadgeluk',
    category: 'general',
  },
  {
    quote:
      'Ik had nooit gedacht dat ik creatief was, tot ik hier mijn eerste workshop volgde. Nu maak ik dingen waar ik trots op ben.',
    name: 'Fatima',
    context: 'Workshop keramiek',
    category: 'general',
    slug: 'fatima-creatief-ontdekt',
    fullStory: `Ik ben opgegroeid met het idee dat creativiteit iets was voor andere mensen. Op school was ik goed in wiskunde en wetenschappen, niet in tekenen of knutselen. Dat label bleef plakken.

Toen een vriendin me meenam naar een keramiekworkshop bij De Maekerij, ging ik met tegenzin. "Ik maak er toch niets van," zei ik nog in de auto. Maar de docente zei iets dat me raakte: "Er bestaat geen fout in keramiek. Elk stuk is uniek, net als jij."

Die middag maakte ik een theekopje. Het was niet perfect — de rand was een beetje scheef en het oor zat iets te laag. Maar toen ik het twee weken later ophaalde, gebakken en geglazuurd, kon ik mijn ogen niet geloven. Ik had dit gemaakt. Met mijn eigen handen.

Sindsdien heb ik workshops gevolgd in macramé, linodruk en zelfs juwelen maken. Elke keer ontdek ik een nieuwe kant van mezelf. Mijn huis staat ondertussen vol met zelfgemaakte spullen en elke keer als iemand vraagt "waar heb je dat gekocht?", zeg ik met een glimlach: "Zelf gemaakt, bij De Maekerij."`,
  },
  {
    quote:
      'De Maekerij is meer dan een creatieve plek — het is een gemeenschap. Ik heb hier vriendinnen voor het leven gemaakt.',
    name: 'Lies',
    context: 'Lid sinds 2023',
    category: 'general',
  },

  // Workshop-specific
  {
    quote:
      'De docente legde alles zo helder uit dat ik na twee uur al een prachtig resultaat had. En de sfeer was zo warm en gezellig!',
    name: 'Noor',
    context: 'Workshop macramé',
    category: 'workshop',
  },
  {
    quote:
      'Ik volgde de workshop samen met mijn dochter. Het was zo fijn om samen iets te maken — een herinnering die we nooit vergeten.',
    name: 'Katrien',
    context: 'Ouder-kind workshop keramiek',
    category: 'workshop',
    slug: 'katrien-moeder-dochter',
    fullStory: `Mijn dochter Emma is tien en zit de hele dag op haar tablet. Ik maakte me zorgen dat we steeds minder samen deden. Toen ik de ouder-kind workshop keramiek zag bij De Maekerij, dacht ik: dit is onze kans.

Emma was eerst sceptisch. "Klei? Echt, mama?" Maar zodra ze haar handen in de klei stak, was ze verkocht. We werkten twee uur lang zij aan zij, lachend om elkaars mislukte pogingen, trots op elkaars kleine successen.

Emma maakte een kommetje voor haar hamster. Ik maakte een plantenpotje dat meer op een asbak leek. Maar dat deed er niet toe. We hadden samen iets gemaakt, samen gelachen, samen iets nieuws geleerd.

Op de terugweg in de auto zei Emma: "Mama, wanneer gaan we weer?" Dat was het mooiste compliment. We hebben sindsdien al drie workshops samen gevolgd. Het is ons ding geworden — geen schermen, geen afleiding, gewoon wij twee en onze handen in de klei.`,
  },
  {
    quote:
      'Na mijn eerste workshop was ik verslaafd. Ik heb er ondertussen al zes gevolgd en elke keer leer ik iets nieuws.',
    name: 'Inge',
    context: 'Workshops textiel & druk',
    category: 'workshop',
  },

  // Maekerscafé-specific
  {
    quote:
      'Het maekerscafé is de perfecte mix van gezelligheid en productiviteit. Ik werk aan mijn project en ondertussen maak ik de leukste gesprekken.',
    name: 'Hilde',
    context: 'Maekerscafé Pen(seel) & Papier',
    category: 'maekerscafe',
  },
  {
    quote:
      'Als nieuwkomer in de stad was het maekerscafé dé manier om mensen te leren kennen. Nu heb ik een hele vriendengroep dankzij De Maekerij.',
    name: 'Yuki',
    context: 'Maekerscafé Creative Meet Up',
    category: 'maekerscafe',
    slug: 'yuki-nieuw-in-de-stad',
    fullStory: `Ik verhuisde vorig jaar vanuit Japan naar België voor het werk van mijn partner. Ik kende niemand, sprak amper Nederlands en voelde me eenzaam. Mijn buurvrouw vertelde me over De Maekerij en het Creative Meet Up maekerscafé.

De eerste keer was spannend. Ik stond met mijn schetsboek in de deuropening en wist niet goed wat te doen. Maar binnen dertig seconden kwam iemand naar me toe: "Hé, welkom! Wat teken je?" Dat was Hilde, die nu een van mijn beste vriendinnen is.

Het maekerscafé werd mijn anker in een nieuwe stad. Elke twee weken kwam ik terug, en elke keer leerde ik nieuwe mensen kennen. We deelden niet alleen creatieve tips, maar ook verhalen over het leven. Iemand hielp me met mijn Nederlands, een ander nodigde me uit voor een etentje.

Nu, een jaar later, heb ik een vriendengroep waar ik nooit van had durven dromen. We zien elkaar niet alleen in De Maekerij, maar ook daarbuiten. En het begon allemaal met een schetsboek en de moed om binnen te stappen.`,
  },
  {
    quote:
      'Elke spelletjesavond is anders. Soms ontdek ik een nieuw spel, soms een nieuw gesprek. Altijd gezellig.',
    name: 'Marieke',
    context: 'Maekerscafé Spelletjesavond',
    category: 'maekerscafe',
  },
]
